import React, { useEffect, useMemo, useRef } from 'react';

import { resolveSiteAssetUrl } from './contentUtils';

function assetUrl(site, value, previewSources) {
  if (!value) {
    return '';
  }

  return previewSources[value] || resolveSiteAssetUrl(site, value);
}

function getMatchStrength(activeFieldPath, targets) {
  if (!activeFieldPath) {
    return 0;
  }

  let strongestMatch = 0;

  targets.forEach((target) => {
    if (activeFieldPath === target) {
      strongestMatch = Math.max(strongestMatch, 2);
      return;
    }

    if (activeFieldPath.startsWith(`${target}.`)) {
      strongestMatch = Math.max(strongestMatch, 1);
    }
  });

  return strongestMatch;
}

function isSectionEnabled(section) {
  return !section || section.enabled !== false;
}

function sectionEyebrow(section, fallback) {
  return section?.eyebrow || fallback;
}

function visibleOpeningHoursDays(openingHours) {
  return Array.isArray(openingHours?.days)
    ? openingHours.days.filter((item) => item && (item.closed || item.opens || item.closes))
    : [];
}

function PreviewFocus({
  activeFieldPath,
  targets,
  label,
  focusPath,
  onSelect,
  variant = 'field',
  className = '',
  scrollOnActive = false,
  children
}) {
  const focusRef = useRef(null);
  const matchStrength = getMatchStrength(activeFieldPath, targets);
  const isActive = matchStrength > 0;
  const isDirectMatch = matchStrength === 2;
  const classes = [
    'preview-focus',
    `preview-focus--${variant}`,
    className,
    isActive ? 'preview-focus--active' : '',
    isDirectMatch ? 'preview-focus--direct' : ''
  ].filter(Boolean).join(' ');

  useEffect(() => {
    if (!scrollOnActive || !isActive || !focusRef.current) {
      return;
    }

    focusRef.current.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth'
    });
  }, [isActive, scrollOnActive, activeFieldPath]);

  function handleActivate(event) {
    if (event) {
      event.stopPropagation();
    }

    if (focusPath && onSelect) {
      onSelect(focusPath);
    }
  }

  return (
    <div
      ref={focusRef}
      className={classes}
      data-preview-label={isActive ? label : undefined}
      role={focusPath ? 'button' : undefined}
      tabIndex={focusPath ? 0 : undefined}
      onClick={focusPath ? handleActivate : undefined}
      onKeyDown={focusPath ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleActivate(event);
        }
      } : undefined}
    >
      {children}
    </div>
  );
}

export default function PreviewPane({ site, content, previewSources, activeFieldPath, onSelectField }) {
  const previewStyle = useMemo(
    () => ({
      '--preview-primary': content?.site?.primaryColor || '#1254c5',
      '--preview-secondary': content?.site?.secondaryColor || '#172033',
      '--preview-image-ratio': content?.media?.imageRatio || '4 / 3',
      '--preview-heading-font': content?.site?.headingFont || 'Georgia, "Times New Roman", serif',
      '--preview-body-font': content?.site?.bodyFont || 'Arial, sans-serif',
      '--preview-header-bg': content?.site?.headerBackgroundColor || 'rgba(252, 251, 247, 0.82)',
      '--preview-main-bg': content?.site?.mainBackgroundColor || 'transparent',
      '--preview-footer-bg': content?.site?.footerBackgroundColor || '#172033'
    }),
    [content]
  );

  if (!content) {
    return (
      <div className="preview-panel shell-card">
        <p>Laddar preview...</p>
      </div>
    );
  }

  const logoUrl = assetUrl(site, content.media.logoUrl, previewSources);
  const logoWidth = content.media.logoWidth || '28px';
  const headerLogoOnly = Boolean(content.media.headerLogoOnly && logoUrl);
  const heroImage = assetUrl(site, content.media.heroImage.url, previewSources);
  const aboutImage = assetUrl(site, content.media.aboutImage.url, previewSources);
  const galleryItems = (content.media.gallery || []).filter((item) => item?.url);
  const socialEntries = Object.entries(content.footer.socialLinks || {}).filter((entry) => Boolean(entry[1]));
  const showHero = isSectionEnabled(content.hero);
  const showIntro = isSectionEnabled(content.intro);
  const showServices = isSectionEnabled(content.services);
  const showAbout = isSectionEnabled(content.about);
  const showUsp = isSectionEnabled(content.usp);
  const showGallery = content.media.galleryEnabled !== false;
  const showTestimonials = isSectionEnabled(content.testimonials);
  const showFaq = isSectionEnabled(content.faq);
  const showContact = isSectionEnabled(content.contact);
  const showFooter = isSectionEnabled(content.footer);
  const openingHoursDays = visibleOpeningHoursDays(content.openingHours);
  const showOpeningHours = isSectionEnabled(content.openingHours) && openingHoursDays.length > 0;

  return (
    <div className="preview-panel shell-card">
      <div className="preview-toolbar">
        <span>Live preview</span>
        <span className="preview-toolbar__hint">
          {activeFieldPath ? `Markerar: ${activeFieldPath}` : 'Fokusera ett fält för att se markering i previewn.'}
        </span>
      </div>
      <div className="site-preview" style={previewStyle}>
        <PreviewFocus
          activeFieldPath={activeFieldPath}
          targets={['site.displayName', 'media.logoUrl']}
          label="Header"
          focusPath="site.displayName"
          onSelect={onSelectField}
          variant="section"
          scrollOnActive
        >
          <header className="site-preview__header">
            <div className="site-preview__brand">
              {logoUrl ? <img src={logoUrl} alt={`${content.site.companyName} logotyp`} className="site-preview__logo" style={{ width: logoWidth }} /> : null}
              {!headerLogoOnly ? <span>{content.site.displayName}</span> : null}
            </div>
            <nav className="site-preview__nav">
              <span>Tjänster</span>
              <span>Om oss</span>
              <span>Kontakt</span>
            </nav>
          </header>
        </PreviewFocus>

        {showHero ? (
        <PreviewFocus
          activeFieldPath={activeFieldPath}
          targets={['hero.eyebrow', 'hero.headline', 'hero.subheadline', 'hero.primaryCtaLabel', 'media.heroImage']}
          label="Hero"
          focusPath="hero.headline"
          onSelect={onSelectField}
          variant="section"
          scrollOnActive
        >
          <section className="site-preview__hero">
            <div>
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['hero.eyebrow']} label="Hero eyebrow" focusPath="hero.eyebrow" onSelect={onSelectField}>
                <p className="site-preview__eyebrow">{content.hero.eyebrow}</p>
              </PreviewFocus>
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['hero.headline']} label="Hero headline" focusPath="hero.headline" onSelect={onSelectField}>
                <h2>{content.hero.headline}</h2>
              </PreviewFocus>
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['hero.subheadline']} label="Hero subheadline" focusPath="hero.subheadline" onSelect={onSelectField}>
                <p className="site-preview__lead">{content.hero.subheadline}</p>
              </PreviewFocus>
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['hero.primaryCtaLabel']} label="Hero CTA" focusPath="hero.primaryCtaLabel" onSelect={onSelectField}>
                <div className="site-preview__ctaRow">
                  <button type="button" className="site-preview__button">{content.hero.primaryCtaLabel}</button>
                </div>
              </PreviewFocus>
            </div>
            <PreviewFocus activeFieldPath={activeFieldPath} targets={['media.heroImage']} label="Hero image" focusPath="media.heroImage.url" onSelect={onSelectField} className="site-preview__visualCard">
              {heroImage ? <img src={heroImage} alt={content.media.heroImage.alt} /> : <div className="site-preview__placeholder">Hero image</div>}
            </PreviewFocus>
          </section>
        </PreviewFocus>
        ) : null}

        {showIntro ? (
        <PreviewFocus activeFieldPath={activeFieldPath} targets={['intro']} label="Introduktion" focusPath="intro.heading" onSelect={onSelectField} variant="section" scrollOnActive>
          <section className="site-preview__section">
            <PreviewFocus activeFieldPath={activeFieldPath} targets={['intro.eyebrow']} label="Intro eyebrow" focusPath="intro.eyebrow" onSelect={onSelectField}>
              <p className="site-preview__eyebrow">{sectionEyebrow(content.intro, 'Introduktion')}</p>
            </PreviewFocus>
            <h3>{content.intro.heading}</h3>
            <p>{content.intro.body}</p>
          </section>
        </PreviewFocus>
        ) : null}

        {showServices ? (
        <PreviewFocus activeFieldPath={activeFieldPath} targets={['services']} label="Tjänster" focusPath="services.heading" onSelect={onSelectField} variant="section" scrollOnActive>
          <section className="site-preview__section site-preview__surface">
            <PreviewFocus activeFieldPath={activeFieldPath} targets={['services.eyebrow']} label="Tjänster eyebrow" focusPath="services.eyebrow" onSelect={onSelectField}>
              <p className="site-preview__eyebrow">{sectionEyebrow(content.services, 'Tjänster')}</p>
            </PreviewFocus>
            <h3>{content.services.heading}</h3>
            <div className="site-preview__grid site-preview__grid--services">
              {content.services.items.map((item, index) => (
                <PreviewFocus
                  key={`${item.title}-${index}`}
                  activeFieldPath={activeFieldPath}
                  targets={[`services.items.${index}`]}
                  label={`Tjänst ${index + 1}`}
                  focusPath={`services.items.${index}.title`}
                  onSelect={onSelectField}
                  className="site-preview__card"
                >
                  {item.image?.url ? (
                    <div className="site-preview__service-image">
                      <img src={assetUrl(site, item.image.url, previewSources)} alt={item.image.alt || item.title} />
                    </div>
                  ) : null}
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </PreviewFocus>
              ))}
            </div>
          </section>
        </PreviewFocus>
        ) : null}

        {showAbout ? (
        <PreviewFocus activeFieldPath={activeFieldPath} targets={['about', 'usp', 'media.aboutImage']} label="Om oss" focusPath="about.heading" onSelect={onSelectField} variant="section" scrollOnActive>
          <section className="site-preview__section site-preview__about">
            <div>
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['about.eyebrow']} label="Om oss eyebrow" focusPath="about.eyebrow" onSelect={onSelectField}>
                <p className="site-preview__eyebrow">{sectionEyebrow(content.about, 'Om oss')}</p>
              </PreviewFocus>
              <h3>{content.about.heading}</h3>
              <p>{content.about.body}</p>
              {showUsp ? (
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['usp']} label="USP" focusPath="usp.heading" onSelect={onSelectField} className="site-preview__usp">
                <strong>{content.usp.heading}</strong>
                <ul>
                  {content.usp.items.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </PreviewFocus>
              ) : null}
            </div>
            <PreviewFocus activeFieldPath={activeFieldPath} targets={['media.aboutImage']} label="About image" focusPath="media.aboutImage.url" onSelect={onSelectField} className="site-preview__visualCard">
              {aboutImage ? <img src={aboutImage} alt={content.media.aboutImage.alt} /> : <div className="site-preview__placeholder">About image</div>}
            </PreviewFocus>
          </section>
        </PreviewFocus>
        ) : null}

        {showGallery && galleryItems.length ? (
          <PreviewFocus activeFieldPath={activeFieldPath} targets={['media.galleryHeading', 'media.gallery']} label="Galleri" focusPath="media.galleryHeading" onSelect={onSelectField} variant="section" scrollOnActive>
            <section className="site-preview__section">
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['media.galleryEyebrow']} label="Galleri eyebrow" focusPath="media.galleryEyebrow" onSelect={onSelectField}>
                <p className="site-preview__eyebrow">{content.media.galleryEyebrow || 'Bilder'}</p>
              </PreviewFocus>
              <h3>{content.media.galleryHeading || 'Inblick i verksamheten'}</h3>
              <div className="site-preview__grid site-preview__grid--gallery">
                {galleryItems.map((item, index) => (
                  <PreviewFocus
                    key={`${item.url}-${index}`}
                    activeFieldPath={activeFieldPath}
                    targets={[`media.gallery.${index}`]}
                    label={`Galleri ${index + 1}`}
                    focusPath={`media.gallery.${index}.url`}
                    onSelect={onSelectField}
                    className="site-preview__card site-preview__card--gallery"
                  >
                    <img src={assetUrl(site, item.url, previewSources)} alt={item.alt} />
                  </PreviewFocus>
                ))}
              </div>
            </section>
          </PreviewFocus>
        ) : null}

        {showTestimonials && content.testimonials.items.length ? (
          <PreviewFocus activeFieldPath={activeFieldPath} targets={['testimonials']} label="Omdömen" focusPath="testimonials.heading" onSelect={onSelectField} variant="section" scrollOnActive>
            <section className="site-preview__section site-preview__surface">
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['testimonials.eyebrow']} label="Omdömen eyebrow" focusPath="testimonials.eyebrow" onSelect={onSelectField}>
                <p className="site-preview__eyebrow">{sectionEyebrow(content.testimonials, 'Omdömen')}</p>
              </PreviewFocus>
              <h3>{content.testimonials.heading}</h3>
              <div className="site-preview__grid site-preview__grid--services">
                {content.testimonials.items.map((item, index) => (
                  <PreviewFocus
                    key={`${item.name}-${index}`}
                    activeFieldPath={activeFieldPath}
                    targets={[`testimonials.items.${index}`]}
                    label={`Omdöme ${index + 1}`}
                    focusPath={`testimonials.items.${index}.name`}
                    onSelect={onSelectField}
                    className="site-preview__card"
                  >
                    <h4>{item.name}</h4>
                    <p>"{item.quote}"</p>
                  </PreviewFocus>
                ))}
              </div>
            </section>
          </PreviewFocus>
        ) : null}

        {showFaq && content.faq.items.length ? (
          <PreviewFocus activeFieldPath={activeFieldPath} targets={['faq']} label="FAQ" focusPath="faq.heading" onSelect={onSelectField} variant="section" scrollOnActive>
            <section className="site-preview__section">
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['faq.eyebrow']} label="FAQ eyebrow" focusPath="faq.eyebrow" onSelect={onSelectField}>
                <p className="site-preview__eyebrow">{sectionEyebrow(content.faq, 'FAQ')}</p>
              </PreviewFocus>
              <h3>{content.faq.heading}</h3>
              <div className="site-preview__stack">
                {content.faq.items.map((item, index) => (
                  <PreviewFocus
                    key={`${item.question}-${index}`}
                    activeFieldPath={activeFieldPath}
                    targets={[`faq.items.${index}`]}
                    label={`FAQ ${index + 1}`}
                    focusPath={`faq.items.${index}.question`}
                    onSelect={onSelectField}
                    className="site-preview__card"
                  >
                    <h4>{item.question}</h4>
                    <p>{item.answer}</p>
                  </PreviewFocus>
                ))}
              </div>
            </section>
          </PreviewFocus>
        ) : null}

        {showContact ? (
        <PreviewFocus activeFieldPath={activeFieldPath} targets={['contact']} label="Kontakt" focusPath="contact.heading" onSelect={onSelectField} variant="section" scrollOnActive>
          <section className="site-preview__section site-preview__surface">
            <PreviewFocus activeFieldPath={activeFieldPath} targets={['contact.eyebrow']} label="Kontakt eyebrow" focusPath="contact.eyebrow" onSelect={onSelectField}>
              <p className="site-preview__eyebrow">{sectionEyebrow(content.contact, 'Kontakt')}</p>
            </PreviewFocus>
            <div className="site-preview__contact">
              <div>
                <h3>{content.contact.heading}</h3>
                <p>{content.contact.body}</p>
              </div>
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['contact.phone', 'contact.email', 'contact.address']} label="Kontaktkort" focusPath="contact.phone" onSelect={onSelectField} className="site-preview__card">
                <p>{content.contact.phone}</p>
                <p>{content.contact.email}</p>
                <p>{content.contact.address}</p>
              </PreviewFocus>
            </div>
          </section>
        </PreviewFocus>
        ) : null}

        {showFooter ? (
        <PreviewFocus activeFieldPath={activeFieldPath} targets={['footer']} label="Footer" focusPath="footer.companyName" onSelect={onSelectField} variant="section" scrollOnActive>
          <footer className="site-preview__footer">
            <div>
              <strong>{content.footer.companyName}</strong>
              <p>{content.footer.tagline}</p>
            </div>
            <div className="site-preview__footerLinks">
              {socialEntries.length
                ? socialEntries.map(([name, value]) => <span key={name}>{name}: {value}</span>)
                : <span>Inga sociala länkar angivna.</span>}
            </div>
            {showOpeningHours ? (
              <PreviewFocus activeFieldPath={activeFieldPath} targets={['openingHours']} label="Öppettider" focusPath="openingHours.heading" onSelect={onSelectField} className="site-preview__openingHours">
                <PreviewFocus activeFieldPath={activeFieldPath} targets={['openingHours.eyebrow']} label="Öppettider eyebrow" focusPath="openingHours.eyebrow" onSelect={onSelectField}>
                  <span className="site-preview__footerMeta">{sectionEyebrow(content.openingHours, 'Öppettider')}</span>
                </PreviewFocus>
                <h4>{content.openingHours.heading || 'Öppettider'}</h4>
                {content.openingHours.body ? <p>{content.openingHours.body}</p> : null}
                <div className="site-preview__hoursList">
                  {openingHoursDays.map((item, index) => (
                    <PreviewFocus
                      key={`${item.day}-${index}`}
                      activeFieldPath={activeFieldPath}
                      targets={[`openingHours.days.${index}`]}
                      label={`${item.day} öppettider`}
                      focusPath={`openingHours.days.${index}.opens`}
                      onSelect={onSelectField}
                      className="site-preview__hoursRow"
                    >
                      <span>{item.day}</span>
                      <span>{item.closed ? 'Stängt' : [item.opens, item.closes].filter(Boolean).join(' - ')}</span>
                    </PreviewFocus>
                  ))}
                </div>
              </PreviewFocus>
            ) : null}
            <p>{content.footer.copyright}</p>
          </footer>
        </PreviewFocus>
        ) : null}
      </div>
    </div>
  );
}
